"use client";

import type { User } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { capture, identify } from "@/lib/analytics/events";
import { testIdentityProps } from "@/lib/analytics/internal-user";
import { onConsentChange, readConsentSync } from "@/lib/consent";
import { readShareArrival } from "@/lib/share";
import { scrubUrlProperties } from "@/lib/analytics-scrub";

const KEY = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
// First-party reverse proxy (rewrite in next.config.ts) so ad blockers can't
// drop our analytics: posthog-js served from eu-assets.i.posthog.com is blocked
// outright by uBlock / EasyPrivacy / Brave, and those visitors were never
// counted. Deliberately NOT read from NEXT_PUBLIC_POSTHOG_HOST — a stale env
// var pointing at the third-party host silently undoes the proxy. To retarget,
// change the rewrite destination instead.
const HOST = "/relay-cmk";

// ── Initialise once, at MODULE LOAD (not inside a useEffect) ─────────────────
// React runs child effects before parent effects, so a $pageview fired from the
// PageviewTracker child would land before a useEffect-based init completed — and
// posthog-js silently drops captures made before .init() runs. Initialising at
// module level is the pattern PostHog documents for Next.js App Router.
//
// TTDSG §25 + GDPR, two-tier by design:
//   DEFAULT (no consent decision yet) — BASIC, COOKIELESS, ANONYMOUS reach:
//     persistence:"memory" + disable_persistence:true write NOTHING to the
//     device, so §25 is not triggered and no consent is required. Page views +
//     visitor counts are lawful under Art. 6(1)(f) legitimate interest,
//     minimised: EU-hosted, no persistent cross-session id, replay OFF. So we
//     capture by DEFAULT — no opt_out_capturing_by_default, no respect_dnt.
//   ON "Accept" (CookieConsent) — PERSISTENT upgrade:
//     persistence → "localStorage+cookie" (cross-session identity + funnel
//     tracing) + session recording on. THAT device storage is what §25 consent
//     authorises.
//   ON "Decline" — opt out of everything (handled just below + in the banner).
//
// Reuses the SAME consent state as the rest of the app (cm.consent.v1 via
// lib/consent.ts) — no new cookie/flag is invented here.
// Portfolio play: NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN is the same value in every
// environment (.env.example ships one slot, not a dev/prod pair), so `pnpm dev`
// on a contributor's machine sends real events into this product's live
// PostHog project under the real token. devotionfund measured this at 26% of
// 30-day pageviews being local-dev traffic, silently inflating every
// traffic-derived decision. Skip init for localhost/127.0.0.1/*.localhost.
function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".localhost")
  );
}

// Portfolio play (CZ-576): automated sessions (headless/driven browsers used for
// prod verification, uptime checks, etc.) set navigator.webdriver=true and would
// otherwise self-pollute visitor counts every time a deploy is verified live.
// Skip init entirely for them rather than filtering after the fact.
//
// finseats has had this guard; this board did not, so every headless check the
// portfolio ran against changemakerhq.org — and it runs them constantly — was
// counted as a visitor. The two boards are one product: a guard on one is a
// guard on both.
//
// Portfolio play (filter-the-control-center-s-own-conceptzoneprobe): the
// Control Center's own uptime prober (ConceptZoneProbe) runs a real headless
// browser with navigator.webdriver suppressed, so the check above alone does
// not catch it — it self-identifies via User-Agent instead, so match on that.
//
// TAG, don't exclude (CM-447, 2026-10-17): this prober IS the traffic the
// Control Center's "analytics-probe-<product>" heartbeat watches to decide
// whether PostHog ingestion is alive. An earlier version of this guard (see
// git history, aa391ee) skipped posthog.init() entirely for this UA, which
// also silenced the only signal that heartbeat reads — it then fired a false
// DOWN alert with the site itself perfectly healthy. Portfolio doctrine
// (re-derived independently on 7 other products the same week) is to tag
// synthetic/monitoring traffic rather than fully exclude it whenever some
// other system's liveness check depends on that same traffic arriving.
// So: let it capture normally, and stamp every event it sends with
// is_uptime_prober so traffic-derived metrics can filter it back out
// downstream without blinding the heartbeat.
const isConceptZoneProbe =
  typeof navigator !== "undefined" &&
  /conceptzoneprobe/i.test(navigator.userAgent);

if (
  typeof window !== "undefined" &&
  KEY &&
  !posthog.__loaded &&
  !navigator.webdriver &&
  !isLocalDevHost(window.location.hostname)
) {
  const consent = readConsentSync(); // "granted" | "denied" | null

  posthog.init(KEY, {
    api_host: HOST,
    ui_host: "https://eu.posthog.com",
    capture_pageview: true, // basic reach on every load; SPA nav handled below
    capture_pageleave: true,
    // Auto-capture uncaught client-side exceptions (window.onerror +
    // unhandledrejection) so a broken page (e.g. a crashed board filter) shows
    // up as an event instead of silently going unreported — there is no
    // Sentry/error-tracking wired up elsewhere in this app.
    capture_exceptions: true,
    // Override posthog-js's identified_only default: its capture() gate
    // short-circuits (silent early-return, no POST) for anonymous visitors
    // unless person profiles are allowed. "always" makes anonymous pageviews +
    // autocapture actually fire — required for cookieless reach measurement.
    person_profiles: "always",
    // Pre-consent tier is cookieless: "memory" + disable_persistence:true means
    // posthog writes NOTHING to the device (ephemeral in-memory distinct_id per
    // load). On "Accept" the banner flips these to localStorage+cookie / false.
    persistence: consent === "granted" ? "localStorage+cookie" : "memory",
    disable_persistence: consent !== "granted",
    disable_session_recording: consent !== "granted",
    // Data minimisation: strip query strings (search terms/filters) from every
    // URL property before it is sent.
    // Data minimisation: strip query strings from every URL-ish property.
    // Pure and tested in src/lib/analytics-scrub.ts — the test runs
    // the scrubber against a real tokenised preferences link rather than
    // grepping for it.
    // Also stamps is_uptime_prober on every event from the Control Center's
    // own ConceptZoneProbe (see isConceptZoneProbe above) so it keeps arriving
    // — feeding the heartbeat — while staying filterable out of real traffic.
    sanitize_properties: (props) =>
      isConceptZoneProbe
        ? scrubUrlProperties({ ...props, is_uptime_prober: true })
        : scrubUrlProperties(props),
    // opt_out_capturing() below only stops EVENT capture — posthog-js still
    // auto-fetches feature flags on load/identify regardless (a real leak:
    // TSF-23 on thesportfund found a declined visitor's browser still POSTing
    // to the flags endpoint). This product has zero useFeatureFlag/getFeatureFlag
    // call sites today, so nothing breaks by disabling it for the non-granted
    // tier; re-enabled + reloaded on "Accept" below.
    advanced_disable_feature_flags_on_first_load: consent !== "granted",
  });

  if (consent === "denied") {
    // Honour an explicit opt-out: stop all capture + wipe any stale storage a
    // previous visit may have left on the device.
    posthog.opt_out_capturing();
    posthog.reset(true);
    // Belt-and-suspenders: advanced_disable_feature_flags_on_first_load above
    // only skips the INITIAL fetch; this stops any later automatic reload too.
    posthog.featureFlags.setReloadingPaused(true);
  }

  // Expose for debugging in dev consoles.
  (window as unknown as { posthog: typeof posthog }).posthog = posthog;
}

/**
 * PostHog is initialised at module load and captures anonymous, cookieless
 * page views for EVERY visitor by default (basic reach — no consent needed).
 * The cookie banner only decides the PERSISTENT tier: "Accept" upgrades to
 * on-device cookies + session replay; "Decline" opts analytics out entirely.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Apply consent decisions that happen AFTER init (e.g. reopening the banner
  // from the footer, or a choice made later in the session). The banner itself
  // also calls posthog directly for immediate effect; this keeps the two in
  // sync and covers the case where posthog wasn't loaded when the banner ran.
  useEffect(() => {
    if (!KEY || !posthog.__loaded) return;
    return onConsentChange((v) => {
      if (v === "granted") {
        posthog.set_config({
          persistence: "localStorage+cookie",
          disable_persistence: false,
        });
        posthog.opt_in_capturing();
        posthog.startSessionRecording?.();
        posthog.featureFlags.setReloadingPaused(false);
        posthog.reloadFeatureFlags();
      } else if (v === "denied") {
        posthog.opt_out_capturing();
        posthog.reset(true);
        posthog.featureFlags.setReloadingPaused(true);
      }
      // v === null (banner reopened): leave current capture running until the
      // visitor makes a choice — basic cookieless reach continues meanwhile.
    });
  }, []);

  if (!KEY) return <>{children}</>;
  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      <AuthIdentify />
      {children}
    </PHProvider>
  );
}

/**
 * Link PostHog events to the signed-in account (CM-10). identify() on session
 * restore / sign-in attributes events to the auth user; reset() on sign-out
 * unlinks the device. Guarded on opt-in so a declined visitor is untouched.
 */
const SIGNUP_CAPTURED_KEY = "cm.signup_completed.v1";
// Supabase fires SIGNED_IN both for a genuine new account AND for every
// session restore, so the event alone can't tell "just signed up" from
// "opened a tab with an old cookie" — no server-side signup hook exists to
// capture from instead (see CM-281). user.created_at is minted server-side at
// account creation and never changes, so "created a few seconds ago" is a
// reliable, auth-logic-free proxy for "this SIGNED_IN is the signup".
const SIGNUP_FRESHNESS_MS = 120_000;

/** CM-281: fire once per device, only when the account is brand new. */
function maybeCaptureSignupCompleted(user: User): void {
  const createdAt = user.created_at ? new Date(user.created_at).getTime() : NaN;
  if (!Number.isFinite(createdAt) || Date.now() - createdAt > SIGNUP_FRESHNESS_MS) return;
  try {
    if (localStorage.getItem(SIGNUP_CAPTURED_KEY)) return;
    localStorage.setItem(SIGNUP_CAPTURED_KEY, "1");
  } catch {
    return; // private mode / storage disabled — skip rather than recapture every load
  }
  capture("sign_up_completed");
}

function identifyUser(user: User): void {
  identify(user.id, {
    email: user.email,
    signup_date: user.created_at,
    is_internal_qa: user.email?.endsWith("@concept.zone") ?? false,
    // What the test-account cohort actually matches (conceptzonetech+ has no
    // @concept.zone to catch) — so our own logins drop out of every number,
    // including the sign_up_completed fired just below.
    ...testIdentityProps(user.email),
  });
  maybeCaptureSignupCompleted(user);
}

function AuthIdentify() {
  useEffect(() => {
    if (!KEY) return;
    const supabase = createClient();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!posthog.__loaded) return;
      if (event === "SIGNED_OUT") {
        posthog.reset();
        return;
      }
      if (session?.user) identifyUser(session.user);
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  return null;
}

function PageviewTracker() {
  const pathname = usePathname();
  // Runs on mount too (React fires every effect at least once), which duplicated
  // the landing $pageview that capture_pageview:true already sent at init — HogQL
  // over a 2-day sample found 375/430 sessions with >=2 pageviews had their FIRST
  // TWO be the identical URL, a median 0.2s apart (some as low as 0ms). This ref
  // skips that first run so only real client-side route changes fire here.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (!KEY || !pathname || !posthog.__loaded) return;
    // Path only — query strings (search terms/filters) are never sent. Fires on
    // client-side route changes; the first load is covered by capture_pageview.
    posthog.capture("$pageview", { $current_url: window.origin + pathname });
  }, [pathname]);

  // /dashboard's tabs (profile/alerts/videos/postings/settings) are all the
  // same pathname, switched via ?tab= — invisible to the tracker above, and
  // sanitize_properties strips query strings from any $current_url/$pathname
  // property anyway. Sent as a plain enum on its own event instead, so the
  // Premium upsell that lives on the settings tab (SubscribeButton in
  // DashboardSettings) is finally measurable: 0 premium_checkout_started could
  // so far mean "nobody clicked" or "nobody ever saw the tab" — no way to tell.
  const searchParams = useSearchParams();
  const dashboardTab =
    pathname === "/dashboard" ? searchParams.get("tab") || "profile" : null;
  useEffect(() => {
    if (!KEY || !dashboardTab || !posthog.__loaded) return;
    posthog.capture("dashboard_tab_view", { tab: dashboardTab });
  }, [dashboardTab]);

  // The other end of the share loop. share_click counts links handed out;
  // without this row there is no way to tell whether anyone ever followed one,
  // so "does sharing work?" stays an opinion. Read from the UTM parameters our
  // own share links carry, so a link copied out of WhatsApp into Slack still
  // counts, and nothing at all fires for organic traffic — an "unknown" bucket
  // that swallowed every normal visit would make the rate meaningless.
  const arrival = readShareArrival(searchParams.toString());
  const arrivalKey = arrival ? `${arrival.surface}|${arrival.channel}` : "";
  useEffect(() => {
    if (!KEY || !arrivalKey || !posthog.__loaded) return;
    const [surface, channel] = arrivalKey.split("|");
    posthog.capture("share_arrival", { surface, channel });
  }, [arrivalKey]);

  return null;
}
